import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "../firebase/auth.tsx";
import "../styles/main.css";
import DetailedTrainingView from "./DetailedTrainingView";
import { useCertificateGenerator } from "./hooks/useCertificateGenerator";
import "bootstrap/dist/css/bootstrap.min.css";

import { authedFetch, AuthError } from "./helpers/requests.tsx";
import { useTranslation } from "react-i18next";

const Main: React.FC = () => {
  const authContext = useAuth();
  const { userId, token, logout } = authContext;

  const [trainingHistory, setTrainingHistory] = useState<any[]>([]);
  const [filteredTrainingHistory, setFilteredTrainingHistory] = useState<any[]>(
    []
  );
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [adminView, setAdminView] = useState(false);
  const [headquarterView, setHeadquarterView] = useState(false);
  const [groupView, setGroupView] = useState(false);
  const [filterPanels, setFilterPanels] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [filters, setFilters] = useState<{ [key: string]: string[] }>({});
  const [searchQueries, setSearchQueries] = useState<{ [key: string]: string }>(
    {}
  );
  const [baseColumnMultiplicator, setBaseColumnMultiplicator] = useState(7);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const { isGenerating, generateCertificate } = useCertificateGenerator();
  const panelRefs = useRef<{ [key: string]: HTMLElement | null }>({});

  const { t, i18n } = useTranslation();

  const trainingResultObject = React.useMemo(() => {
    if (!selectedRow?.trainingResult) {
      return null;
    }
    try {
      return typeof selectedRow.trainingResult === "string"
        ? JSON.parse(selectedRow.trainingResult)
        : selectedRow.trainingResult;
    } catch (error) {
      console.error("Invalid JSON string format:", error);
      return null;
    }
  }, [selectedRow]);

  const detailsProp = React.useMemo(() => {
    if (!selectedRow) return null;

    return {
        trainingResult: trainingResultObject,
        sensorUsed: selectedRow.sensorUsed ?? false
    };
  }, [selectedRow, trainingResultObject]);

  useEffect(() => {
    const fetchData = async () => {
      if (userId && token) {
        setLoading(true);
        try {
          await showTrainingsHistory(userId);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();

    const handleClickOutside = (event: MouseEvent) => {
      if (
        Object.keys(panelRefs.current).some((key) => {
          const panel = panelRefs.current[key];
          return panel && !panel.contains(event.target as Node);
        })
      ) {
        setFilterPanels({});
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [userId, token]);

  // It should be before apply filters to update filtered list correctly
  useEffect(() => {
    setFilteredTrainingHistory(trainingHistory.slice(0, currentPage * 100));
  }, [trainingHistory, currentPage]);

  useEffect(() => {
    applyFilters();
  }, [filters, currentPage]);

  const showTrainingsHistory = async (userId: string) => {
    try {
      const trainingsData = await authedFetch(
        `api/requests/getTrainingsHistory`,
        {
          method: "POST",
          body: JSON.stringify(userId),
        }
      );

      if (!trainingsData || !trainingsData.history) {
        setTrainingHistory([]);
        setFilteredTrainingHistory([]);
        return;
      }

      setTrainingHistory(trainingsData.history);
      setFilteredTrainingHistory(trainingsData.history);

      switch (trainingsData.view) {
        case "admin":
          setAdminView(true);
          setBaseColumnMultiplicator(10);
        case "headquarterAdmin":
          setHeadquarterView(true);
          setBaseColumnMultiplicator(9);
        case "groupAdmin":
          setGroupView(true);
          setBaseColumnMultiplicator(8);
      }
    } catch (error) {
      if (error instanceof AuthError) {
        logout();
      } else {
        console.error("Error getting trainings history:", error);
      }
    }
  };

  const handleRowClick = (index: number) => {
    setSelectedRow(filteredTrainingHistory[index]);
  };

  const getLocalizedResult = (value) => {
    if (typeof value !== "string") {
      return value || "";
    }

    const match = value.match(/Failed\s\((\d+)\/(\d+)\)/);
    if (match) {
      const current = match[1];
      const total = match[2];
      return t("FailedWithNumbers", { current, total });
    }
    return t(value);
  };

  const getLocalizedMistake = (value) => {
    if (typeof value !== "string") {
      return value || "";
    }

    // This function is for the format "key, variable1, variable2"
    // e.g. "Mistake_Wrong_Step, Step_OpenMouth, Step_ListenBreathing"
    const parts = value.split(',').map(part => part.trim());
    if (parts.length === 3 && parts[0] === 'Mistake_Wrong_Step') {
      const expected = parts[1];
      const made = parts[2];
      return t("Mistake_Wrong_Step", { expected: t(expected), made: t(made) });
    }
    else if (parts.length === 3 && parts[0] === 'Mistake_CPR_Count') {
      const correctPresses = parts[1];
      const totalPresses = parts[2];
      return t("Mistake_CPR_Count", { correctPresses, totalPresses });
    }
    return t(value);
  };

  const toggleFilterPanel = (column: string) => {
    setFilterPanels((prevState) => {
      const newState = Object.keys(prevState).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {} as { [key: string]: boolean });
      return {
        ...newState,
        [column]: !prevState[column],
      };
    });
    setSearchQueries((prevState) => ({
      ...prevState,
      [column]: "",
    }));
  };

  const handleFilterChange = (column: string, value: string) => {
    setFilters((prevState) => {
      const columnFilters = prevState[column] || [];
      if (columnFilters.includes(value)) {
        return {
          ...prevState,
          [column]: columnFilters.filter((v) => v !== value),
        };
      } else {
        return {
          ...prevState,
          [column]: [...columnFilters, value],
        };
      }
    });
  };

  const applyFilters = () => {
    const filteredData = trainingHistory.filter((record) => {
      return Object.keys(filters).every((column) => {
        const selectedFilters = filters[column];
        if (!selectedFilters || selectedFilters.length === 0) {
          return true;
        }

        let recordValue;
        if (column === "firstName" && record.isDeleted) {
          recordValue = t("Deleted_User");
        } else {
          recordValue =
            column === "trainingResult"
              ? record.trainingResultValue
              : record[column];
        }

        const isRecordEmpty = recordValue == null || recordValue === "";

        const emptyFilterSelected = selectedFilters.includes("");

        const nonEmptyMatch =
          !isRecordEmpty && selectedFilters.includes(recordValue.toString());

        const emptyMatch = isRecordEmpty && emptyFilterSelected;

        return nonEmptyMatch || emptyMatch;
      });
    });
    setFilteredTrainingHistory(filteredData.slice(0, currentPage * 100));
  };

  const handleFilterSearch = (column: string, query: string) => {
    setSearchQueries((prevState) => ({
      ...prevState,
      [column]: query,
    }));
  };

  const renderFilterPanel = (column: string, lastColumn: boolean = false) => {
    const query = searchQueries[column]?.toLowerCase() || "";

    const hasEmptyValues = trainingHistory.some((record) => {
      const val =
        column === "trainingResult"
          ? record.trainingResultValue
          : record[column];
      return val == null || val === "";
    });

    const originalUniqueValues = Array.from(
      new Set(
        trainingHistory.map((record) => {
          if (column === "firstName" && record.isDeleted) {
            return t("Deleted_User");
          }
          return column === "trainingResult"
            ? record.trainingResultValue
            : record[column];
        })
      )
    ).filter((value) => value != null && value !== "");

    let filteredOriginalValues = originalUniqueValues.filter(
      (originalValue) => {
        const displayValue =
          column === "trainingResult"
            ? getLocalizedResult(originalValue)
            : t(originalValue);
        return displayValue.toString().toLowerCase().includes(query);
      }
    );

    filteredOriginalValues.sort((a, b) => {
      const displayA =
        column === "trainingResult" ? getLocalizedResult(a) : t(a);
      const displayB =
        column === "trainingResult" ? getLocalizedResult(b) : t(b);
      return displayA.localeCompare(displayB);
    });

    const handleSelectAll = (checked: boolean) => {
      const valuesToModify = [...filteredOriginalValues];
      if (hasEmptyValues) {
        valuesToModify.push("");
      }

      if (checked) {
        setFilters((prevState) => ({
          ...prevState,
          [column]: Array.from(
            new Set([...(prevState[column] || []), ...valuesToModify])
          ),
        }));
      } else {
        setFilters((prevState) => ({
          ...prevState,
          [column]: (prevState[column] || []).filter(
            (v) => !valuesToModify.includes(v)
          ),
        }));
      }
    };

    const allVisibleChecked =
      filteredOriginalValues.length > 0 &&
      filteredOriginalValues.every((val) => filters[column]?.includes(val)) &&
      (!hasEmptyValues || (filters[column]?.includes("") ?? false));

    return (
      <div
        ref={(el) => (panelRefs.current[column] = el)}
        className={lastColumn ? "filter-last-panel" : "filter-panel"}
        style={{
          maxHeight: "calc(100vh / 2)",
          overflowY: "auto",
          scrollbarWidth: "thin",
          scrollbarColor: "#EFF0F2 transparent",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <input
            type="text"
            placeholder={t("search")}
            style={{
              width: "94%",
              backgroundColor: "#eff0f2",
              border: "none",
              outline: "none",
              borderRadius: "10px",
              color: "#404040",
              height: "2.7vh",
              maxHeight: "30px",
              fontSize: "1em",
              paddingLeft: "10px",
              marginTop: "10px",
            }}
            value={query}
            onChange={(e) => handleFilterSearch(column, e.target.value)}
          />
        </div>
        {query === "" && (
          <ul
            style={{
              listStyleType: "none",
              padding: 0,
              marginBottom: "1vh",
              marginTop: "1vh",
            }}
          >
            <li
              key={"selectAll"}
              className="filters-form-check filters-checkbox"
            >
              <label
                className="filters-checkbox-label"
                style={{ display: "inline-block" }}
              >
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="agreeCheckbox"
                  checked={allVisibleChecked}
                  onChange={(e) => {
                    handleSelectAll(e.target.checked);
                  }}
                  style={{ display: "none" }}
                />
                <span
                  className="filters-checkmark"
                  style={{ backgroundColor: "white" }}
                ></span>
                <span className="form-check-label">{t("Select_all")}</span>
              </label>
            </li>
            {query === "" && hasEmptyValues && (
              <li
                key="empty-filter"
                className="filters-form-check filters-checkbox"
              >
                <label
                  className="filters-checkbox-label"
                  style={{ display: "inline-block" }}
                >
                  <input
                    type="checkbox"
                    checked={filters[column]?.includes("") || false}
                    onChange={() => handleFilterChange(column, "")}
                  />
                  <span
                    className="filters-checkmark"
                    style={{ backgroundColor: "white" }}
                  ></span>
                  <span className="form-check-label">{t("empty")}</span>
                </label>
              </li>
            )}
          </ul>
        )}
        <ul
          style={{
            listStyleType: "none",
            padding: 0,
            marginBottom: "1vh",
            marginTop: "1vh",
          }}
        >
          {filteredOriginalValues.map((originalValue) => (
            <li
              key={originalValue}
              className="filters-form-check filters-checkbox"
            >
              <label
                className="filters-checkbox-label"
                style={{ display: "inline-block" }}
              >
                <input
                  type="checkbox"
                  checked={
                    filters[column]?.includes(originalValue.toString()) || false
                  }
                  onChange={() =>
                    handleFilterChange(column, originalValue.toString())
                  }
                />
                <span
                  className="filters-checkmark"
                  style={{ backgroundColor: "white" }}
                ></span>
                <span className="form-check-label">
                  {column === "trainingResult"
                    ? getLocalizedResult(originalValue) || t("empty")
                    : t(originalValue) || t("empty")}
                </span>
              </label>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderTrainingDetailsModal = () => {
    if (!selectedRow) return null;

    const hasDetailedResults =
      trainingResultObject && trainingResultObject.meanRate !== undefined;

    const hasMistakes =
      trainingResultObject &&
      trainingResultObject.results &&
      trainingResultObject.results.length > 0;

     const mistakes = trainingResultObject?.results?.filter((r) => !r.result) || [];

    return (
      <div className="confirmation-modal">
        <div
          className="modal-content"
          style={{
            maxWidth: hasDetailedResults ? "1200px" : "500px",
            maxHeight: "800px",
            transition: "max-width 0.3s ease-in-out",
            padding: 0
          }}
        >
          <div className="detailed-view-layout">
            <div className="detailed-training-wrapper">
              {hasDetailedResults ? (
                <DetailedTrainingView details={detailsProp} />
              ) : (
                <p>{t("Training details")}</p>
              )}
              <div className="detailed-view-main-text">
                <div>
                  {t("Device name")}:{" "}
                  <span style={{ fontFamily: "VirtualLabQuickSandMedium" }}>
                    {selectedRow.trainingName}
                  </span>
                </div>
                <div>
                  {t("Training type")}:{" "}
                  <span style={{ fontFamily: "VirtualLabQuickSandMedium" }}>
                    {selectedRow.trainingType}
                  </span>
                </div>
                <div>
                  {t("Start time")}:{" "}
                  <span style={{ fontFamily: "VirtualLabQuickSandMedium" }}>
                    {formatFirestoreTimestamp(selectedRow.trainingTime)}
                  </span>
                </div>
                <div>
                  {t("Duration")}:{" "}
                  <span style={{ fontFamily: "VirtualLabQuickSandMedium" }}>
                    {selectedRow.trainingDurationFormatted}
                  </span>
                </div>
                <div>
                  {t("Result")}:{" "}
                  <span style={{ fontFamily: "VirtualLabQuickSandMedium" }}>
                    {getLocalizedResult(selectedRow.trainingResultValue)}
                  </span>
                </div>
                {hasMistakes && trainingResultObject.results[0].text !== "noCounter" && !hasDetailedResults && (
                    <>
                      <br />
                      <div>
                        {t("Details")}:
                        <ul>
                          {trainingResultObject.results.map((result, index) => (
                            <li
                              key={index}
                              style={{
                                color: result.result ? "#44963C" : "#A63939",
                              }}
                            >
                              {getLocalizedMistake(result.text)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </>
                  )}
              </div>

              <div className="button-container">
                <button
                  type="button"
                  className="buttonCancel btn btn-primary"
                  onClick={() => setSelectedRow(null)}
                >
                  {t("Close")}
                </button>
                {hasDetailedResults && (
                  <button
                    type="button"
                    className="buttonAcceptGreen btn btn-primary"
                    onClick={() => generateCertificate(selectedRow, userId)}
                    disabled={isGenerating}
                    style={{
                      marginLeft: "10px",
                      opacity: isGenerating ? 0.6 : 1,
                      cursor: isGenerating ? "not-allowed" : "pointer",
                    }}
                  >
                    {isGenerating ? t("Generating...") : t("Download certificate")}
                  </button>
                )}
              </div>
            </div>
            {hasMistakes && (
              <div className="mistakes-column">
                <div className="section-header mistakes-header">{t("Mistakes")}
                </div>
                <div className="mistakes-list">
                  {mistakes.map((mistake, index) => (
                    <div key={index} className="mistake-item">
                      <span className="mistake-icon">•</span>
                      <span className="mistake-text">{getLocalizedMistake(mistake.text)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const debugStyle = {
    position: "fixed",
    top: "0",
    left: "0",
    zIndex: 1000,
    backgroundColor: "white",
  };

  return (
    <div className="main-container">
      {loading ? (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            fontSize: "0.7rem",
          }}
        >
          {t("Loading...")}
        </div>
      ) : (
        <>
          <div
            style={{
              marginTop: "80px",
              marginLeft: "50px",
              marginRight: "40px",
              height: "100%",
              minHeight: "calc(100vh - 120px)",
              overflowY: "auto",
              scrollbarWidth: "thin",
              scrollbarColor: "#183a68 transparent",
            }}
          >
            {/* Separate table header - don't find other way to avoid overlapping header on scroll */}
            <div
              style={{
                position: "sticky",
                top: "0",
                zIndex: "1",
                backgroundColor: "transparent",
                border: "none",
                marginLeft: "12px",
                marginRight: "11px",
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
              }}
            >
              <table
                className="table"
                style={{
                  borderColor: "transparent",
                  marginBottom: "0px",
                  tableLayout: "fixed",
                  color: "#183a68",
                }}
              >
                <thead>
                  <tr>
                    {(adminView || headquarterView || groupView) && (
                      <th
                        scope="col"
                        style={{
                          width: `calc(100% / ${baseColumnMultiplicator})`,
                          fontWeight: "normal",
                        }}
                      >
                        <span
                          onClick={() => toggleFilterPanel("firstName")}
                          style={{
                            cursor: "pointer",
                          }}
                        >
                          {t("User")}
                          <span
                            style={{
                              fontSize: "1.3em",
                              fontWeight: "bold",
                            }}
                          >
                            {" "}
                            &#9013;
                          </span>
                        </span>
                        {filterPanels["firstName"] &&
                          renderFilterPanel("firstName")}
                      </th>
                    )}
                    {adminView && (
                      <th
                        scope="col"
                        style={{
                          width: `calc(100% / ${baseColumnMultiplicator - 1})`,
                          fontWeight: "normal",
                        }}
                      >
                        <span
                          onClick={() => toggleFilterPanel("headquarterName")}
                          style={{
                            cursor: "pointer",
                          }}
                        >
                          {t("Headquarter")}
                          <span
                            style={{
                              fontSize: "1.3em",
                              fontWeight: "bold",
                            }}
                          >
                            {" "}
                            &#9013;
                          </span>
                        </span>
                        {filterPanels["headquarterName"] &&
                          renderFilterPanel("headquarterName")}
                      </th>
                    )}
                    {(adminView || headquarterView) && (
                      <th
                        scope="col"
                        style={{
                          width: `calc(100% / ${baseColumnMultiplicator - 1})`,
                          fontWeight: "normal",
                        }}
                      >
                        <span
                          onClick={() => toggleFilterPanel("groupName")}
                          style={{
                            cursor: "pointer",
                          }}
                        >
                          {t("Group")}
                          <span
                            style={{
                              fontSize: "1.3em",
                              fontWeight: "bold",
                            }}
                          >
                            {" "}
                            &#9013;
                          </span>
                        </span>
                        {filterPanels["groupName"] &&
                          renderFilterPanel("groupName")}
                      </th>
                    )}
                    <th
                      scope="col"
                      style={{
                        width: `calc(100% / ${baseColumnMultiplicator - 3})`,
                        fontWeight: "normal",
                      }}
                    >
                      <span
                        onClick={() => toggleFilterPanel("trainingType")}
                        style={{
                          cursor: "pointer",
                        }}
                      >
                        {t("Training_type")}
                        <span
                          style={{
                            fontSize: "1.3em",
                            fontWeight: "bold",
                          }}
                        >
                          {" "}
                          &#9013;
                        </span>
                      </span>
                      {filterPanels["trainingType"] &&
                        renderFilterPanel("trainingType")}
                    </th>
                    <th
                      scope="col"
                      style={{
                        width: `calc(100% / ${baseColumnMultiplicator - 1})`,
                        fontWeight: "normal",
                      }}
                    >
                      <span
                        onClick={() => toggleFilterPanel("trainingName")}
                        style={{
                          cursor: "pointer",
                        }}
                      >
                        {t("Environment")}
                        <span
                          style={{
                            fontSize: "1.3em",
                            fontWeight: "bold",
                          }}
                        >
                          {" "}
                          &#9013;
                        </span>
                      </span>
                      {filterPanels["trainingName"] &&
                        renderFilterPanel("trainingName")}
                    </th>
                    <th
                      scope="col"
                      style={{
                        width: `calc(100% / ${baseColumnMultiplicator - 3})`,
                        fontWeight: "normal",
                      }}
                    >
                      {t("Start_time")}
                    </th>
                    <th
                      scope="col"
                      style={{
                        width: `calc(100% / ${baseColumnMultiplicator + 4})`,
                        fontWeight: "normal",
                      }}
                    >
                      {t("Duration")}
                    </th>
                    <th
                      scope="col"
                      style={{
                        width: `calc(100% / ${baseColumnMultiplicator + 4})`,
                        fontWeight: "normal",
                      }}
                    >
                      <span
                        onClick={() => toggleFilterPanel("trainingResult")}
                        style={{
                          cursor: "pointer",
                        }}
                      >
                        {t("Result")}
                        <span
                          style={{
                            fontSize: "1.3em",
                            fontWeight: "bold",
                          }}
                        >
                          {" "}
                          &#9013;
                        </span>
                      </span>
                      {filterPanels["trainingResult"] &&
                        renderFilterPanel("trainingResult", true)}
                    </th>
                    <th className="detailsButtonWrapper"></th>
                  </tr>
                </thead>
              </table>
            </div>

            {/* Table with restricted height and scrollbar */}
            <div
              style={{
                maxHeight: "calc(100vh - 165px)",
                overflowY: "auto",
                scrollbarWidth: "thin",
                scrollbarColor: "#183a68 transparent",
              }}
            >
              <div className="container-fluid" style={{ paddingRight: "0px" }}>
                <table
                  className="table"
                  style={{
                    tableLayout: "fixed",
                  }}
                >
                  <tbody>
                    {filteredTrainingHistory.map(
                      (trainingRecord: any, index: number) => (
                        <tr
                          key={`${formatFirestoreTimestamp(
                            trainingRecord.trainingTime
                          )}-${trainingRecord.trainingId}-${index}`}
                          style={{
                            backgroundColor:
                              index % 2 === 0 ? "#dbdfe7" : "transparent",
                          }}
                        >
                          {(adminView || headquarterView || groupView) && (
                            <td
                              style={{
                                width: `calc(100% / ${baseColumnMultiplicator})`,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                verticalAlign: "middle",
                              }}
                            >
                              <div className="containerHeader">
                                <img
                                  src="/images/Icon_User_Simple.png"
                                  style={{
                                    width: "2vh",
                                    minWidth: "10px",
                                    marginLeft: "0.3vh",
                                    marginRight: "1.5vh",
                                  }}
                                />
                                <div className="userName">
                                  {trainingRecord.isDeleted ? (
                                    t("Deleted_User")
                                  ) : (
                                    <>
                                      {trainingRecord.firstName}
                                      <br />
                                      {trainingRecord.lastName}
                                    </>
                                  )}
                                </div>
                              </div>
                            </td>
                          )}
                          {adminView && (
                            <td
                              style={{
                                width: `calc(100% / ${
                                  baseColumnMultiplicator - 1
                                })`,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                verticalAlign: "middle",
                              }}
                            >
                              {trainingRecord.headquarterName}
                            </td>
                          )}
                          {(adminView || headquarterView) && (
                            <td
                              style={{
                                width: `calc(100% / ${
                                  baseColumnMultiplicator - 1
                                })`,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                verticalAlign: "middle",
                                lineHeight: 1,
                              }}
                            >
                              {trainingRecord.groupName}
                              {trainingRecord.groupName && (
                                <span
                                  style={{
                                    color: "#183a68",
                                  }}
                                >
                                  &nbsp;|{" "}
                                  {t("country_" + trainingRecord.countryCode)}
                                </span>
                              )}
                            </td>
                          )}
                          <td
                            style={{
                              width: `calc(100% / ${
                                baseColumnMultiplicator - 3
                              })`,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              verticalAlign: "middle",
                              lineHeight: 1,
                            }}
                          >
                            {trainingRecord.trainingType}
                          </td>
                          <td
                            style={{
                              width: `calc(100% / ${
                                baseColumnMultiplicator - 1
                              })`,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              verticalAlign: "middle",
                              lineHeight: 1,
                            }}
                          >
                            {trainingRecord.trainingName}
                          </td>
                          <td
                            style={{
                              width: `calc(100% / ${
                                baseColumnMultiplicator - 3
                              })`,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              verticalAlign: "middle",
                              lineHeight: 1,
                            }}
                          >
                            {formatFirestoreTimestamp(
                              trainingRecord.trainingTime
                            )}
                          </td>
                          <td
                            style={{
                              width: `calc(100% / ${
                                baseColumnMultiplicator + 4
                              })`,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              verticalAlign: "middle",
                              lineHeight: 1,
                            }}
                          >
                            {trainingRecord.trainingDurationFormatted}
                          </td>
                          <td
                            style={{
                              color: trainingRecord.totalResult
                                ? "#44963C"
                                : "#A63939",
                              width: `calc(100% / ${
                                baseColumnMultiplicator + 4
                              })`,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              verticalAlign: "middle",
                              lineHeight: 1,
                            }}
                          >
                            {getLocalizedResult(
                              trainingRecord.trainingResultValue
                            )}
                          </td>
                          <td className="detailsButtonWrapper">
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={() => handleRowClick(index)}
                            >
                              {t("Details")}
                            </button>
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
                {filteredTrainingHistory.length < trainingHistory.length && (
                  <div style={{ textAlign: "center" }}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      style={{
                        backgroundColor: "#183a68",
                        borderColor: "#183a68",
                        borderRadius: "10px",
                        marginRight: "10px",
                        marginBottom: "10px",
                        fontSize: "1em",
                      }}
                    >
                      {t("Show_More")}
                    </button>
                  </div>
                )}
              </div>
            </div>
            {selectedRow && renderTrainingDetailsModal()}
          </div>
        </>
      )}{" "}
    </div>
  );
};

const formatFirestoreTimestamp = (timestampInput: any): string => {
  const timestampMs = Number(timestampInput);

  if (!isNaN(timestampMs) && timestampMs > 0) {
    const date = new Date(timestampMs);

    const options: Intl.DateTimeFormatOptions = {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    };

    return date.toLocaleString("en-GB", options).replace(",", "");
  }

  return "Invalid Date";
};

export default Main;
